import { useState } from "react";
import { BarChart3, Database, LockKeyhole, ShieldCheck, TableProperties } from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { apiFetch } from "@/lib/api-client";
import { SkeletonCard } from "@/components/skeleton";
import { formatNumber } from "@/lib/i18n";

type SourceStatus = "available" | "empty" | "planned";
type VisibilityBucket = "customer_visible" | "operator_only" | "hidden";
type FilterOperator = "eq" | "in" | "gte" | "lte" | "exists";

interface ProductReportSourceColumn {
  key: string;
  label: string;
  type?: "string" | "number" | "datetime" | "enum" | "boolean";
  piiClassification: "safe" | "aggregate" | "operator_only";
}

interface ProductReportSourceFilter {
  key: string;
  label: string;
  operators: FilterOperator[];
}

interface ProductReportSourceDefinition {
  key: string;
  label: string;
  description: string;
  status: SourceStatus;
  requiredModule: string | null;
  allowedColumns: ProductReportSourceColumn[];
  allowedFilters: ProductReportSourceFilter[];
  redactionPolicy: "metadata_only" | "aggregate_only" | "not_available";
}

type PreviewCellValue = string | number | boolean | null;

interface ProductReportSourcePreview {
  readOnly: true;
  sourceKey: string;
  columns: ProductReportSourceColumn[];
  rows: Array<Record<string, PreviewCellValue>>;
  counts: {
    requestedLimit: number;
    effectiveLimit: number;
    returnedRows: number;
    availableRows: number;
  };
  warnings: string[];
}

interface ProductReportQueryPreview extends ProductReportSourcePreview {
  queryMode: "validated_preview";
  appliedQuery: {
    columns: string[];
    filters: Array<{
      column: string;
      operator: FilterOperator;
      value?: string | number | boolean | Array<string | number | boolean> | null;
    }>;
    sort: {
      column: string;
      direction?: "asc" | "desc";
    } | null;
  };
}

interface ProductSubjectVisibilityProductApp {
  productAppKey: string;
  productAppLabel: string;
  total: number;
  buckets: Record<VisibilityBucket, number>;
  byStatus: Array<{
    status: string;
    visibility: VisibilityBucket;
    count: number;
  }>;
}

interface ProductReportSourceCatalog {
  readOnly: true;
  catalogVersion: string;
  constraints: {
    arbitrarySql: false;
    rawMetadata: false;
    rawPageContent: false;
    piiValues: false;
  };
  sources: ProductReportSourceDefinition[];
  availability: {
    courses: number;
    courseLessons: number;
    courseEnrollments: number;
    diagnosticSummaries: number;
    productSubjects: number;
  };
  subjectVisibility: {
    total: number;
    buckets: Record<VisibilityBucket, number>;
    byProductApp: ProductSubjectVisibilityProductApp[];
  };
}

const STATUS_LABELS: Record<SourceStatus, string> = {
  available: "Available",
  empty: "No rows yet",
  planned: "Planned",
};

const VISIBILITY_LABELS: Record<VisibilityBucket, string> = {
  customer_visible: "Customer visible",
  operator_only: "Operator only",
  hidden: "Hidden",
};

const FILTER_OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: "equals",
  in: "is one of",
  gte: "at least",
  lte: "at most",
  exists: "is present",
};

function availabilityFor(source: ProductReportSourceDefinition, catalog: ProductReportSourceCatalog): number | null {
  switch (source.key) {
    case "courses":
      return catalog.availability.courses;
    case "course_lessons":
      return catalog.availability.courseLessons;
    case "course_enrollments":
      return catalog.availability.courseEnrollments;
    case "diagnostic_summaries":
      return catalog.availability.diagnosticSummaries;
    case "product_subjects":
      return catalog.availability.productSubjects;
    default:
      return null;
  }
}

function statusClass(status: SourceStatus): string {
  switch (status) {
    case "available":
      return "border-[var(--color-success)]/25 bg-[var(--color-success)]/10 text-[var(--color-success)]";
    case "planned":
      return "border-[var(--color-warning)]/25 bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
    default:
      return "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-muted-foreground)]";
  }
}

function visibilityClass(bucket: VisibilityBucket): string {
  switch (bucket) {
    case "customer_visible":
      return "bg-[var(--color-success)]/10 text-[var(--color-success)]";
    case "operator_only":
      return "bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
    default:
      return "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]";
  }
}

function SourceCard({
  source,
  catalog,
  isSelected,
  onSelect,
}: {
  source: ProductReportSourceDefinition;
  catalog: ProductReportSourceCatalog;
  isSelected: boolean;
  onSelect: (sourceKey: string) => void;
}) {
  const availability = availabilityFor(source, catalog);
  const canPreview = source.status === "available";

  return (
    <article className={`rounded-[1.75rem] border bg-[var(--color-card)] p-5 shadow-sm ${
      isSelected ? "border-[var(--color-primary)] ring-2 ring-[var(--color-primary)]/15" : "border-[var(--color-border)]"
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{source.label}</h2>
          <p className="mt-1 text-sm leading-6 text-[var(--color-muted-foreground)]">{source.description}</p>
        </div>
        <span className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${statusClass(source.status)}`}>
          {STATUS_LABELS[source.status]}
        </span>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl bg-[var(--color-surface)] p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Rows</p>
          <p className="mt-1 text-xl font-semibold text-[var(--color-foreground)]">
            {availability === null ? "Not exposed" : formatNumber(availability)}
          </p>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Module</p>
          <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{source.requiredModule ?? "Platform"}</p>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Policy</p>
          <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{source.redactionPolicy.replaceAll("_", " ")}</p>
        </div>
      </div>

      {source.allowedColumns.length > 0 ? (
        <div className="mt-5">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Approved fields</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {source.allowedColumns.map((column) => (
              <span
                key={column.key}
                className="rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]"
              >
                {column.label} · {column.piiClassification.replaceAll("_", " ")}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
          This source is visible for roadmap planning only. It is not queryable until the generic contract is reviewed.
        </div>
      )}

      <button
        type="button"
        disabled={!canPreview}
        onClick={() => onSelect(source.key)}
        className="mt-5 w-full rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-2.5 text-sm font-semibold text-[var(--color-foreground)] transition hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {canPreview ? isSelected ? "Preview selected" : "Preview safe rows" : "Preview unavailable"}
      </button>
    </article>
  );
}

function formatPreviewValue(value: PreviewCellValue): string {
  if (value === null) return "Not set";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return formatNumber(value);
  return value;
}

function parseScalarFilterValue(rawValue: string, columnType: ProductReportSourceColumn["type"] | undefined): string | number | boolean {
  if (columnType === "number") {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      throw new Error("Numeric filters require a valid number");
    }
    return parsed;
  }
  if (columnType === "boolean") {
    return rawValue === "true";
  }
  return rawValue;
}

function parseFilterValue(
  operator: FilterOperator,
  rawValue: string,
  columnType: ProductReportSourceColumn["type"] | undefined,
): string | number | boolean | Array<string | number | boolean> | undefined {
  if (operator === "exists") return rawValue !== "false";
  const trimmed = rawValue.trim();
  if (!trimmed) return undefined;
  if (operator === "in") {
    return trimmed
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => parseScalarFilterValue(value, columnType));
  }
  return parseScalarFilterValue(trimmed, columnType);
}

function PreviewTable({ preview }: { preview: ProductReportSourcePreview | ProductReportQueryPreview }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--color-border)]">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[var(--color-border)] text-sm">
          <thead className="bg-[var(--color-surface)]">
            <tr>
              {preview.columns.map((column) => (
                <th key={column.key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)] bg-[var(--color-background)]">
            {preview.rows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(preview.columns.length, 1)} className="px-4 py-6 text-center text-[var(--color-muted-foreground)]">
                  No preview rows returned.
                </td>
              </tr>
            ) : preview.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {preview.columns.map((column) => (
                  <td key={column.key} className="whitespace-nowrap px-4 py-3 text-[var(--color-foreground)]">
                    {formatPreviewValue(row[column.key] ?? null)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SourcePreviewPanel({
  source,
  preview,
  isLoading,
  error,
  refetch,
}: {
  source: ProductReportSourceDefinition | null;
  preview: ProductReportSourcePreview | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}) {
  return (
    <section className="rounded-[1.75rem] border border-[var(--color-border)] bg-[var(--color-card)] p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-[var(--color-primary)]/10 p-2 text-[var(--color-primary)]">
          <TableProperties className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Safe source preview</h2>
          <p className="mt-1 text-sm leading-6 text-[var(--color-muted-foreground)]">
            Bounded rows come from the same audited API/MCP source contract. No arbitrary SQL, raw metadata, raw page content, or subject identifiers.
          </p>
        </div>
      </div>

      {!source ? (
        <div className="mt-5 rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
          No available source can be previewed for this tenant yet.
        </div>
      ) : isLoading ? (
        <SkeletonCard className="mt-5 h-52 rounded-2xl" />
      ) : error ? (
        <div className="mt-5 rounded-2xl border border-[var(--color-destructive)]/25 bg-[var(--color-destructive)]/5 p-4">
          <p className="text-sm font-semibold text-[var(--color-foreground)]">Preview failed for {source.label}</p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{error}</p>
          <button
            type="button"
            onClick={refetch}
            className="mt-3 rounded-xl bg-[var(--color-foreground)] px-4 py-2 text-sm font-medium text-[var(--color-background)]"
          >
            Retry preview
          </button>
        </div>
      ) : preview ? (
        <div className="mt-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-[var(--color-surface)] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Source</p>
              <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{source.label}</p>
            </div>
            <div className="rounded-2xl bg-[var(--color-surface)] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Rows shown</p>
              <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">
                {formatNumber(preview.counts.returnedRows)} of {formatNumber(preview.counts.availableRows)}
              </p>
            </div>
            <div className="rounded-2xl bg-[var(--color-surface)] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Limit</p>
              <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{formatNumber(preview.counts.effectiveLimit)}</p>
            </div>
          </div>

          {preview.warnings.length > 0 ? (
            <div className="rounded-2xl border border-[var(--color-warning)]/25 bg-[var(--color-warning)]/10 p-3 text-sm text-[var(--color-warning)]">
              {preview.warnings.join(" ")}
            </div>
          ) : null}

          <PreviewTable preview={preview} />
        </div>
      ) : null}
    </section>
  );
}

function QueryBuilderPanel({ source }: { source: ProductReportSourceDefinition | null }) {
  const initialColumns = source?.allowedColumns.slice(0, 4).map((column) => column.key) ?? [];
  const firstFilter = source?.allowedFilters[0] ?? null;
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialColumns);
  const [limit, setLimit] = useState(10);
  const [sortColumn, setSortColumn] = useState<string>(initialColumns[0] ?? "");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [filterColumn, setFilterColumn] = useState<string>(firstFilter?.key ?? "");
  const [filterOperator, setFilterOperator] = useState<FilterOperator>(firstFilter?.operators[0] ?? "eq");
  const [filterValue, setFilterValue] = useState("");
  const [preview, setPreview] = useState<ProductReportQueryPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  if (!source) {
    return (
      <section className="rounded-[1.75rem] border border-dashed border-[var(--color-border)] bg-[var(--color-card)] p-5 text-sm text-[var(--color-muted-foreground)]">
        Select an available source before building a report query.
      </section>
    );
  }

  const selectedFilter = source.allowedFilters.find((filter) => filter.key === filterColumn) ?? firstFilter;
  const selectedFilterColumn = selectedFilter
    ? source.allowedColumns.find((column) => column.key === selectedFilter.key)
    : null;
  const effectiveFilterOperator = selectedFilter?.operators.includes(filterOperator)
    ? filterOperator
    : selectedFilter?.operators[0] ?? "eq";

  const toggleColumn = (columnKey: string) => {
    setSelectedColumns((current) => {
      if (current.includes(columnKey)) {
        const next = current.filter((key) => key !== columnKey);
        return next.length > 0 ? next : current;
      }
      return [...current, columnKey].slice(0, 8);
    });
  };

  const runQueryPreview = async () => {
    setIsRunning(true);
    setError(null);
    try {
      const parsedFilterValue = selectedFilter
        ? parseFilterValue(effectiveFilterOperator, filterValue, selectedFilterColumn?.type)
        : undefined;
      const filters = selectedFilter && (effectiveFilterOperator === "exists" || parsedFilterValue !== undefined)
        ? [{
          column: selectedFilter.key,
          operator: effectiveFilterOperator,
          value: parsedFilterValue,
        }]
        : [];
      const response = await apiFetch<{ data: ProductReportQueryPreview }>("/v1/product-reports/query/preview", {
        method: "POST",
        body: JSON.stringify({
          sourceKey: source.key,
          columns: selectedColumns,
          filters,
          sort: sortColumn ? { column: sortColumn, direction: sortDirection } : null,
          limit,
        }),
      });
      setPreview(response.data);
    } catch (caughtError) {
      setPreview(null);
      setError(caughtError instanceof Error ? caughtError.message : "Query preview failed");
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <section className="rounded-[1.75rem] border border-[var(--color-border)] bg-[var(--color-card)] p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-[var(--color-primary)]/10 p-2 text-[var(--color-primary)]">
          <BarChart3 className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Validated query builder</h2>
          <p className="mt-1 text-sm leading-6 text-[var(--color-muted-foreground)]">
            Build a report preview from approved columns, filters, sort, and row limits. The API still rejects anything outside the source contract.
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Columns</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {source.allowedColumns.map((column) => (
            <label key={column.key} className="flex items-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]">
              <input
                type="checkbox"
                checked={selectedColumns.includes(column.key)}
                onChange={() => toggleColumn(column.key)}
                className="h-4 w-4 rounded border-[var(--color-border)]"
              />
              <span>{column.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="space-y-2 text-sm">
          <span className="font-semibold text-[var(--color-foreground)]">Sort by</span>
          <select
            value={sortColumn}
            onChange={(event) => setSortColumn(event.target.value)}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
          >
            {source.allowedColumns.map((column) => (
              <option key={column.key} value={column.key}>{column.label}</option>
            ))}
          </select>
        </label>
        <label className="space-y-2 text-sm">
          <span className="font-semibold text-[var(--color-foreground)]">Direction</span>
          <select
            value={sortDirection}
            onChange={(event) => setSortDirection(event.target.value === "desc" ? "desc" : "asc")}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </label>
      </div>

      {selectedFilter ? (
        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_1.25fr]">
          <label className="space-y-2 text-sm">
            <span className="font-semibold text-[var(--color-foreground)]">Filter</span>
            <select
              value={selectedFilter.key}
              onChange={(event) => {
                const nextFilter = source.allowedFilters.find((filter) => filter.key === event.target.value);
                setFilterColumn(event.target.value);
                setFilterOperator(nextFilter?.operators[0] ?? "eq");
              }}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
            >
              {source.allowedFilters.map((filter) => (
                <option key={filter.key} value={filter.key}>{filter.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-2 text-sm">
            <span className="font-semibold text-[var(--color-foreground)]">Operator</span>
            <select
              value={effectiveFilterOperator}
              onChange={(event) => setFilterOperator(event.target.value as FilterOperator)}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)]"
            >
              {selectedFilter.operators.map((operator) => (
                <option key={operator} value={operator}>{FILTER_OPERATOR_LABELS[operator]}</option>
              ))}
            </select>
          </label>
          <label className="space-y-2 text-sm">
            <span className="font-semibold text-[var(--color-foreground)]">Value</span>
            <input
              value={filterValue}
              onChange={(event) => setFilterValue(event.target.value)}
              disabled={effectiveFilterOperator === "exists"}
              placeholder={selectedFilterColumn?.type === "number" ? "50" : effectiveFilterOperator === "in" ? "draft, published" : "published"}
              className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)] disabled:opacity-60"
            />
          </label>
        </div>
      ) : null}

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <label className="space-y-2 text-sm">
          <span className="font-semibold text-[var(--color-foreground)]">Limit</span>
          <select
            value={limit}
            onChange={(event) => setLimit(Number(event.target.value))}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-[var(--color-foreground)] sm:w-40"
          >
            {[5, 10, 25, 50].map((value) => (
              <option key={value} value={value}>{value} rows</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={runQueryPreview}
          disabled={isRunning || selectedColumns.length === 0}
          className="rounded-2xl bg-[var(--color-foreground)] px-5 py-2.5 text-sm font-semibold text-[var(--color-background)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isRunning ? "Running preview..." : "Run validated query preview"}
        </button>
      </div>

      <div className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-3 text-xs text-[var(--color-muted-foreground)]">
        No custom SQL, raw metadata, page content, or subject/contact identifiers are accepted by this builder.
      </div>

      {error ? (
        <div className="mt-4 rounded-2xl border border-[var(--color-destructive)]/25 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      ) : null}

      {preview ? (
        <div className="mt-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-[var(--color-surface)] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Mode</p>
              <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{preview.queryMode.replaceAll("_", " ")}</p>
            </div>
            <div className="rounded-2xl bg-[var(--color-surface)] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Columns</p>
              <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{formatNumber(preview.columns.length)}</p>
            </div>
            <div className="rounded-2xl bg-[var(--color-surface)] p-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Rows</p>
              <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{formatNumber(preview.counts.returnedRows)}</p>
            </div>
          </div>
          {preview.warnings.length > 0 ? (
            <div className="rounded-2xl border border-[var(--color-warning)]/25 bg-[var(--color-warning)]/10 p-3 text-sm text-[var(--color-warning)]">
              {preview.warnings.join(" ")}
            </div>
          ) : null}
          <PreviewTable preview={preview} />
        </div>
      ) : null}
    </section>
  );
}

function VisibilityPanel({ catalog }: { catalog: ProductReportSourceCatalog }) {
  return (
    <section className="rounded-[1.75rem] border border-[var(--color-border)] bg-[var(--color-card)] p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-[var(--color-primary)]/10 p-2 text-[var(--color-primary)]">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Product subject visibility</h2>
          <p className="mt-1 text-sm leading-6 text-[var(--color-muted-foreground)]">
            Aggregate-only counts show what can feed reports without exposing subject keys, identities, raw metadata, or contact PII.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {(Object.keys(VISIBILITY_LABELS) as VisibilityBucket[]).map((bucket) => (
          <div key={bucket} className={`rounded-2xl px-4 py-3 ${visibilityClass(bucket)}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.14em]">{VISIBILITY_LABELS[bucket]}</p>
            <p className="mt-1 text-2xl font-semibold">{formatNumber(catalog.subjectVisibility.buckets[bucket])}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {catalog.subjectVisibility.byProductApp.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
            No product subjects are available yet for this tenant.
          </div>
        ) : catalog.subjectVisibility.byProductApp.map((app) => (
          <article key={app.productAppKey} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold text-[var(--color-foreground)]">{app.productAppLabel}</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">{app.productAppKey}</p>
              </div>
              <p className="text-sm font-semibold text-[var(--color-foreground)]">{formatNumber(app.total)} subjects</p>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {app.byStatus.map((status) => (
                <span key={status.status} className={`rounded-full px-3 py-1 text-xs font-semibold ${visibilityClass(status.visibility)}`}>
                  {status.status}: {formatNumber(status.count)}
                </span>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function ProductReportSourcePage() {
  const [selectedSourceKey, setSelectedSourceKey] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = useApi<ProductReportSourceCatalog>("/v1/product-reports/sources");
  const firstAvailableSource = data?.sources.find((source) => source.status === "available") ?? null;
  const effectiveSelectedSourceKey = selectedSourceKey ?? firstAvailableSource?.key ?? null;
  const selectedSource = data?.sources.find((source) => source.key === effectiveSelectedSourceKey && source.status === "available") ?? firstAvailableSource;
  const previewPath = selectedSource
    ? `/v1/product-reports/sources/${encodeURIComponent(selectedSource.key)}/preview?limit=10`
    : "";
  const {
    data: preview,
    isLoading: isPreviewLoading,
    error: previewError,
    refetch: refetchPreview,
  } = useApi<ProductReportSourcePreview>(previewPath, { enabled: Boolean(selectedSource) });

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="overflow-hidden rounded-[2rem] border border-[var(--color-border)] bg-[linear-gradient(135deg,var(--color-card),var(--color-surface))] p-6 shadow-sm sm:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[var(--color-primary)]/15 bg-[var(--color-card)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
              <Database className="h-3.5 w-3.5" />
              Report source catalog
            </span>
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-[-0.025em] text-[var(--color-foreground)] sm:text-4xl">
                Safe data sources for tenant reports
              </h1>
              <p className="max-w-3xl text-sm leading-6 text-[var(--color-muted-foreground)] sm:text-base">
                Stero reports start from reviewed read models. This page shows what the current tenant can use, what stays operator-only, and what is deliberately not exposed.
              </p>
            </div>
          </div>
          <div className="grid gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 sm:min-w-[20rem] sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Mode</p>
              <p className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">Read only</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Version</p>
              <p className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">{data?.catalogVersion ?? "Loading"}</p>
            </div>
          </div>
        </div>
      </section>

      {isLoading ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} className="h-64 rounded-[1.75rem]" />
          ))}
        </div>
      ) : error ? (
        <section className="rounded-2xl border border-[var(--color-destructive)]/25 bg-[var(--color-destructive)]/5 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-semibold text-[var(--color-foreground)]">Report source catalog is unavailable</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{error}</p>
            </div>
            <button
              type="button"
              onClick={refetch}
              className="rounded-xl bg-[var(--color-foreground)] px-4 py-2 text-sm font-medium text-[var(--color-background)]"
            >
              Retry
            </button>
          </div>
        </section>
      ) : data ? (
        <>
          <section className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
              <BarChart3 className="h-5 w-5 text-[var(--color-primary)]" />
              <p className="mt-3 text-2xl font-semibold text-[var(--color-foreground)]">{formatNumber(data.sources.filter((source) => source.status === "available").length)}</p>
              <p className="text-sm text-[var(--color-muted-foreground)]">Available report sources</p>
            </div>
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
              <ShieldCheck className="h-5 w-5 text-[var(--color-success)]" />
              <p className="mt-3 text-2xl font-semibold text-[var(--color-foreground)]">{formatNumber(data.subjectVisibility.total)}</p>
              <p className="text-sm text-[var(--color-muted-foreground)]">Product subjects counted safely</p>
            </div>
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
              <LockKeyhole className="h-5 w-5 text-[var(--color-warning)]" />
              <p className="mt-3 text-2xl font-semibold text-[var(--color-foreground)]">
                {data.constraints.arbitrarySql || data.constraints.piiValues ? "Unsafe" : "Locked"}
              </p>
              <p className="text-sm text-[var(--color-muted-foreground)]">No arbitrary SQL or raw PII values</p>
            </div>
          </section>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(22rem,0.75fr)]">
            <section className="grid gap-4 lg:grid-cols-2">
              {data.sources.map((source) => (
                <SourceCard
                  key={source.key}
                  source={source}
                  catalog={data}
                  isSelected={source.key === selectedSource?.key}
                  onSelect={setSelectedSourceKey}
                />
              ))}
            </section>
            <div className="space-y-5">
              <SourcePreviewPanel
                source={selectedSource}
                preview={preview}
                isLoading={isPreviewLoading}
                error={previewError}
                refetch={refetchPreview}
              />
              <QueryBuilderPanel key={selectedSource?.key ?? "no-source"} source={selectedSource} />
              <VisibilityPanel catalog={data} />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
